import * as THREE from 'three'
import * as _ from 'lodash'
import { action, observable, computed, reaction } from 'mobx'
import { RootStore } from '@app/models/RootStore'
import { LapTimesPane } from '@app/views/LapTimesPane'
import { QualifyingPane } from '@app/views/QualifyingPane'
import { PitStopPane } from '@app/views/PitStopPane'
import { Scatterplot } from '@app/views/Scatterplot'

export class Renderer {
  rootStore: RootStore
  renderer: THREE.WebGLRenderer
  camera: THREE.OrthographicCamera
  lapTimesPane: LapTimesPane
  qualifyingPane: QualifyingPane
  pitStopPane: PitStopPane
  scene: THREE.Scene
  raycaster: THREE.Raycaster
  renderPending = false
  @observable initialized = false
  @observable mouseX: number = null
  @observable mouseY: number = null

  constructor({
    rootStore,
    canvas,
  }: {
    rootStore: RootStore
    canvas: HTMLCanvasElement
  }) {
    this.rootStore = rootStore
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
    })

    this.scene = new THREE.Scene()
    this.renderer.setClearColor(0xffffff, 0.0)
    this.camera = new THREE.OrthographicCamera(0, 1, 0, 1)
    this.raycaster = new THREE.Raycaster()
    this.raycaster.params.Points.threshold = 10

    this.lapTimesPane = new LapTimesPane({ rootStore })
    this.qualifyingPane = new QualifyingPane({ rootStore })
    this.pitStopPane = new PitStopPane({ rootStore })

    reaction(
      () =>
        this.initialized &&
        this.rootStore.initialized && {
          plots: this.scatterplots,
          mouseX: this.mouseX,
          mouseY: this.mouseY,
        },
      () => {
        this.render()
      }
    )
  }

  @action resize({
    width,
    height,
    pixelRatio,
    qualifyingRect,
    lapTimesRect,
    pitStopRect,
  }: {
    width: number
    height: number
    pixelRatio: number
    qualifyingRect: ClientRect
    lapTimesRect: ClientRect
    pitStopRect: ClientRect
  }) {
    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(pixelRatio === 1 ? 1.5 : pixelRatio)

    this.camera.position.set(0, 0, 300)
    this.camera.lookAt(0, 0, 0)
    this.camera.left = 0
    this.camera.right = width
    this.camera.top = 0
    this.camera.bottom = height
    this.camera.near = 0.1
    this.camera.far = 1000
    this.camera.updateProjectionMatrix()
    this.camera.updateMatrixWorld()

    this.lapTimesPane.resize({
      rect: lapTimesRect,
      pixelRatio: this.renderer.getPixelRatio(),
    })
    this.qualifyingPane.resize({
      rect: qualifyingRect,
      pixelRatio: this.renderer.getPixelRatio(),
    })
    this.pitStopPane.resize({
      rect: pitStopRect,
      pixelRatio: this.renderer.getPixelRatio(),
    })

    this.initialized = true
    this.render()
  }

  pointToViewport(point: THREE.Vector3) {
    const size = this.renderer.getSize(new THREE.Vector2())
    const projected = point.clone().project(this.camera)

    return {
      x: ((projected.x + 1) / 2) * size.x,
      y: (-(projected.y - 1) / 2) * size.y,
    }
  }

  @computed get scatterplots() {
    return [
      this.lapTimesPane.scatterplot,
      this.qualifyingPane.scatterplot,
      this.pitStopPane.scatterplot,
    ]
  }

  @computed get labels() {
    if (!this.rootStore.initialized) {
      return []
    }

    return _.flatten(_.map(this.scatterplots, 'labels')).map(label => ({
      ...this.pointToViewport(label.point),
      ...label,
    }))
  }

  @action mouseMove(x: number, y: number) {
    this.mouseX = x
    this.mouseY = y
  }

  @action mouseLeave() {
    this.mouseX = null
    this.mouseY = null
  }

  @action raycast() {
    if (this.mouseX === null || this.mouseY === null) {
      return
    }

    const size = this.renderer.getSize(new THREE.Vector2())
    const mouseVector = new THREE.Vector3(
      (this.mouseX / size.x) * 2 - 1,
      -(this.mouseY / size.y) * 2 + 1,
      0.5
    )

    this.raycaster.setFromCamera(mouseVector, this.camera)

    const barIntersections = this.raycaster.intersectObjects(
      _.map(this.scatterplots, 'bars')
    )

    if (barIntersections.length > 0) {
      const intersection = barIntersections[0]
      this.setHoveredDataset(intersection, (plot: Scatterplot) => plot.bars)
      this.rootStore.hoveredLap = Math.floor(intersection.faceIndex / 2)
    } else {
      this.rootStore.hoveredDataset = null
      this.rootStore.hoveredLap = null
    }
  }

  @action setHoveredDataset(
    intersection: any,
    objectLookup: (plot: Scatterplot) => THREE.Object3D
  ) {
    if (intersection.object === objectLookup(this.lapTimesPane.scatterplot)) {
      this.rootStore.hoveredDataset = 'lapTimes'
    } else if (
      intersection.object === objectLookup(this.qualifyingPane.scatterplot)
    ) {
      this.rootStore.hoveredDataset = 'qualifyingTimes'
    } else if (
      intersection.object === objectLookup(this.pitStopPane.scatterplot)
    ) {
      this.rootStore.hoveredDataset = 'pitStopTimes'
    }
  }

  @action clearHover() {
    this.rootStore.hoveredDataset = null
    this.rootStore.hoveredLap = null
  }

  click() {
    this.raycast()

    if (this.rootStore.hoveredDataset !== null) {
      this.rootStore.selectedDataset = this.rootStore.hoveredDataset
    }

    if (this.rootStore.hoveredDriver !== null) {
      this.rootStore.selectedDriver = this.rootStore.hoveredDriver
    }

    if (this.rootStore.hoveredLap !== null) {
      this.rootStore.selectedLap = this.rootStore.hoveredLap
    }
  }

  render() {
    if (this.renderPending === false) {
      this.renderPending = true
      window.requestAnimationFrame(() => {
        this.renderPending = false
        this.renderNow()
      })
    }
  }

  renderNow() {
    if (this.initialized && this.rootStore.initialized) {
      this.raycast()
      this.scene.remove(...this.scene.children)
      this.scene.add(this.camera)
      this.scene.add(..._.map(this.scatterplots, 'scene'))
      this.renderer.render(this.scene, this.camera)
    }
  }
}
